import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import { pageContext, get } from 'ctm-util-functions';
import { useBrandContentFactory } from 'ctm-app/client/hooks/useBrandContentFactory';
import { FeaturesCell } from './features_cell/features_cell';
import styles from './features_list.css';
import { FeatureListAccordion } from './features_list_accordion';

const FeaturesList = (props) => {
    const { enableResultsFeatureAccordion } = useBrandContentFactory();
    /**
     * Get a state value via a string state key
     * @param {String} stateKey - the state key used to get state data
     * @returns {String} the content found at the state key
     */
    const getValueByString = (stateKey) => {
        // deal with inclusions ask about format
        const { quote } = props;
        const productMeta = quote.get('productMeta');
        if (pageContext.getVertical() === 'car-insurance' && productMeta.get('features') && stateKey.includes('features.')) {
            const featureStateKey = stateKey.replace('features.', '');
            // Convert Features Immutable List of Maps to Immutable Map of Maps
            const features = Immutable.Map(productMeta.get('features').map((feature) => [feature.get('code'), feature]));
            return get(features, featureStateKey, null);
        }
        return get(productMeta, stateKey, null);
    };

    /**
     * Map through an array of statekeys and resolve each one, then return the new array
     * @param {Array} arrayOfStateKeys - the array of state keys
     * @returns {Array} the new array with values mapped from state
     */
    const getValuesByArray = (arrayOfStateKeys) => {
        return arrayOfStateKeys.map((key) => getValueByString(key)).filter((val) => val !== null);
    };

    /**
     * Map through array of objects returning the description and the resolved stateKey as amount
     * @param {Array} customItems - Objects in the format {description, stateKey}
     * @returns {Array} the new array with values mapped from state
     */
    const getCustomContent = (customItems) => {
        const { quote } = props;
        const isCarJourney = pageContext.getVertical() === 'car-insurance';
        const carProductHasGlassExcess = isCarJourney && quote.get('productMeta').get('glassExcess');
        return customItems.map((item) => {
            return item.stateKey === 'glassExcess' && isCarJourney && !carProductHasGlassExcess
                ? null : { description: item.description, amount: getValueByString(item.stateKey) };
        }).filter((obj) => obj !== null);
    };

    /**
     * Full width labels, which are rendered before feature cells on certain breakpoints
     * @param {Number} index - the column index
     * @param {String} feature - the current feature in the iteration
     * @param {Label} label - the feature's label found in the vertical's JSON resultsFeatureDetails file
     * @param {Object} rules - label specific visibility rules
     * @param {Object} modalProps - the props used to render the modal
     * @param {helpText} helpText - the props used to render the helpText
     * @returns {Node} a configured full width label features cell
     */
    const createFullWidthLabel = (index, feature, label, rules, modalProps, helpText) => {
        const isFirstColumn = index === 0;
        const { numberOfQuotes, columnsPerPage } = props;
        const lastCell = (index + 1) === numberOfQuotes;
        if (label && label === 'hidden') return null;
        return (
            <FeaturesCell
                columnsPerPage={columnsPerPage}
                numberOfQuotes={numberOfQuotes}
                label={isFirstColumn}
                lastCell={lastCell}
                key={`${feature}_label`}
                helpText={helpText}
                id={`${feature}_label`}
                balanceHeight
                format={isFirstColumn ? 'fullWidthLabel' : 'labelCell'}
                content={label}
                className={isFirstColumn ? 'labelCellCentered' : 'labelCell'}
                rules={rules}
                modalProps={modalProps}
            />
        );
    };

    /**
     * Creates a default feature cell
     * @param {Number} index - the column index
     * @param {String} feature - the current feature in the iteration
     * @param {Label} label - the feature's label found in the vertical's JSON resultsFeatureDetails file
     * @param {Object} rules - label specific visibility rules
     * @param {Boolean} fullWidthLabels - are we rendering full width labels
     * @param {String} format - the feature cell's format
     * @param {Boolean} balanceHeight - should this cell's height be balanced
     * @param {String|Number} content - the content within the actual feature cell
     * @param {Boolean} accordion - whether this cell is part of an accordion row
     * @param {String} accordionLabel - the label to be rendered within the closed accordion state
     * @param {String} defaultValue - the default value to show for html list cells
     * @param {Object} modalProps - the props used to render the modal
     * @returns {Node} a configured featured cell
     */
    const createFeaturesCell = (index, feature, label, rules, fullWidthLabels, format, balanceHeight, content, accordion, accordionLabel, defaultValue, modalProps) => {
        const className = fullWidthLabels && format === 'labelCell' ? 'hidden' : format;
        const { numberOfQuotes } = props;
        const lastCell = (index + 1) === numberOfQuotes;
        if (label && label === 'hidden') return null;
        return (
            <FeaturesCell
                key={feature}
                id={feature}
                balanceHeight={balanceHeight}
                label={false}
                lastCell={lastCell}
                format={format}
                content={content}
                className={className}
                rules={rules}
                accordion={accordion}
                accordionLabel={accordionLabel}
                defaultValue={defaultValue}
                modalProps={modalProps}
            />
        );
    };

    /**
     * Iterates through a schema and maps each feature to a statekey, which is then passed to a feature cell component
     * This is the root method that will iterate through the entire resultsFeatureDetails schema
     * @param {Boolean} fullWidthLabels - whether or not to include full width labels for each feature cell - this is decided based on screen size
     * @returns {Array} an array of feature cells with mapped data and variants
     */
    const renderQuoteFeatureDetails = (fullWidthLabels) => {
        const resultsFeatureDetails = pageContext.props.verticalJSON.resultsFeatureDetails;
        return buildQuoteFeaturesFromList(fullWidthLabels, resultsFeatureDetails);
    };

    /**
     * Takes a list of feature details and maps them to feature cells
     * This is a recursive method that will iterate through a list of result feature details
     * This gets recursively invoked when a feature has an array of items e.g. feature_list_accordion
     * @param {Boolean} fullWidthLabels - whether or not to include full width labels for each feature cell - this is decided based on screen size
     * @param {Array} resultsFeatureDetails - the feature details to be mapped to feature cells
     * @returns {Array} an array of feature cells with mapped data and variants
     */
    const buildQuoteFeaturesFromList = (fullWidthLabels, resultsFeatureDetails) => {
        const features = [];
        const { index } = props;
        for (const feature in resultsFeatureDetails) {
            if (resultsFeatureDetails.hasOwnProperty(feature)) {
                const { format, stateKey, rules, label, balanceHeight, helpText, accordion, accordionLabel, defaultValue, customItems, modalProps } = resultsFeatureDetails[feature];
                let content = label;
                if (format === 'feature_list_accordion') {
                    // if the feature is an accordion, extract the list of items
                    const accordianItems = resultsFeatureDetails[feature]?.items;
                    const id = resultsFeatureDetails[feature]?.id;
                    // If the brand has the feature accordion enabled, render the accordion
                    // This check allows us to maintain one configuration for the JSON and ignore the accordion if the brand does want one
                    if (enableResultsFeatureAccordion()) {
                        features.push(
                            <FeatureListAccordion id={id}>
                                {[...buildQuoteFeaturesFromList(fullWidthLabels, accordianItems)]}
                            </FeatureListAccordion>
                        );
                        continue;
                    } else {
                        // If the accordion feature is disabled we want to render the items as normal
                        // Recursively call this method with the list of items that were contained in the accordion
                        // This will render the items as normal feature labels
                        features.push(buildQuoteFeaturesFromList(fullWidthLabels, accordianItems));
                    }
                } else if (format === 'custom') {
                    content = getCustomContent(customItems);
                } else if (typeof stateKey === 'string') {
                    content = getValueByString(stateKey);
                } else if (Array.isArray(stateKey)) {
                    content = getValuesByArray(stateKey);
                }

                if (label && label === 'hidden') continue;
                // create a full width label and add it before the features cell if required
                // @todo the label !== Insured amount is a hack for the moment need to come up with a way to omit certain labels
                if (fullWidthLabels && label && label !== 'Insured Amount') {
                    features.push(
                        createFullWidthLabel(index, feature, label, rules, modalProps, helpText)
                    );
                }
                features.push(
                    createFeaturesCell(index, feature, label, rules, fullWidthLabels, format, balanceHeight, content, accordion, accordionLabel, defaultValue, modalProps)
                );
            }
        }
        return features;
    };

    /**
     * Iterates through a schema and maps a label for each feature
     * This is the root method that will iterate through the entire resultsFeatureDetails schema
     * Build an array of feature labels, including multi rows, full width label cells, and accordion type styles
     * @returns {Array} an array of FeatureCells configured to be used as labels for each feature
     */
    const renderFeaturesDetailLabels = () => {
        const resultsFeatures = pageContext.props.verticalJSON.resultsFeatureDetails;
        return buildFeatureLabelsFromList(resultsFeatures);
    };

    /**
     * Takes a list of feature details and maps them to feature cells configured as labels
     * This is a recursive method that will iterate through a list of result feature details
     * The recursion is invoked when a feature has an array of items e.g. feature_list_accordion
     * @param {*} resultsFeatureDetails the feature details to be mapped to feature cells
     * @returns {Array} an array of FeatureCells configured to be used as labels for each feature
     */
    const buildFeatureLabelsFromList = (resultsFeatureDetails) => {
        const featureLabels = [];
        for (const feature in resultsFeatureDetails) {
            if (resultsFeatureDetails.hasOwnProperty(feature)) {
                const { format, rules, label, balanceHeight, helpText, accordion, modalProps } = resultsFeatureDetails[feature];
                const labelCell = format === 'labelCell';
                if (label && label === 'hidden') {
                    continue;
                } else if (format === 'feature_list_accordion') {
                    // if the feature is an accordion, extract the list of items
                    const accordianItems = resultsFeatureDetails[feature]?.items;
                    const id = resultsFeatureDetails[feature]?.id;
                    // If the brand has the feature accordion enabled, render the accordion
                    // This check allows us to maintain one configuration for the JSON and ignore the accordion if the brand does want one
                    if (enableResultsFeatureAccordion()) {
                        featureLabels.push(
                            <FeatureListAccordion id={id}>
                                {[...buildFeatureLabelsFromList(accordianItems)]}
                            </FeatureListAccordion>
                        );
                    } else {
                        // If the accordion feature is disabled we want to render the items as normal
                        // Recursively call this method with the list of items that were contained in the accordion
                        // This will render the items as normal feature labels
                        featureLabels.push(buildFeatureLabelsFromList(accordianItems));
                    }
                } else {
                    featureLabels.push(
                        <FeaturesCell
                            label
                            key={feature}
                            id={feature}
                            balanceHeight={balanceHeight}
                            helpText={helpText}
                            format={'labelCell'}
                            content={label}
                            className={labelCell ? 'labelCell' : 'label'}
                            rules={rules}
                            accordion={accordion}
                            modalProps={modalProps}
                        />);
                }
            }
        }
        return featureLabels;
    };

    /**
     * Renders labels or features depending on the isLabels prop, to eliminate duplication and keep cells consistent
     * @returns {Node} FeaturesList - a list of features or featureLabels
     */
    const { isLabels, fullWidthLabels } = props;

    return (
        <div className={styles.featuresList}>
            { isLabels ? renderFeaturesDetailLabels() : renderQuoteFeatureDetails(fullWidthLabels) }
        </div>
    );
};

FeaturesList.propTypes = {
    /* should this list render labels */
    isLabels: PropTypes.bool,
    /* @todo remove this once we have the quote model completed and set up */
    /* the quote to get product meta, and the features list */
    quote: PropTypes.object,
    fullWidthLabels: PropTypes.bool,
    index: PropTypes.number,
    numberOfQuotes: PropTypes.number,
    columnsPerPage: PropTypes.number
};

export { FeaturesList };
